import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { EmptyKrampSpecial, KrampSpecial } from '../kramp-special';
import { ReservedSpecialSku } from '../../reserved-special-sku';
import { KrampSpecialCode } from './kramp-special-code';
import { KRAMP_SPECIAL_CODES } from './kramp-special-codes';
import { Profile } from 'src/app/general-components/profile/profile';
import { AuthService } from 'src/app/auth/auth.service';
import { Supplier } from '../../suppliers/supplier';

@Component({
  selector: 'app-new-kramp-special',
  templateUrl: './new-kramp-special.component.html',
  styleUrls: ['./new-kramp-special.component.scss'],
})
export class NewKrampSpecialComponent implements OnInit {
  @Input('suppliers') suppliers: Supplier[] = [];

  @Input('reservedSku') reservedSku: ReservedSpecialSku = { id: 0, sku: '' };
  @Output('keepInputs') keepInputs: EventEmitter<boolean> = new EventEmitter();
  @Output('createNewSpecial') createNewSpecial: EventEmitter<KrampSpecial> =
    new EventEmitter();

  @ViewChild('description') description!: NgModel;
  @ViewChild('specialCode') specialCode!: NgModel;
  @ViewChild('supplier') supplier!: NgModel;
  @ViewChild('supplierSku') supplierSku!: NgModel;
  @ViewChild('purchasePrice') purchasePrice!: NgModel;
  @ViewChild('supplierPrice') supplierPrice!: NgModel;
  @ViewChild('salesPrice') salesPrice!: NgModel;
  @ViewChild('orderUnit') orderUnit!: NgModel;
  @ViewChild('deliveryDays') deliveryDays!: NgModel;

  showForm = false;

  newSpecial: KrampSpecial = EmptyKrampSpecial();
  profile: Profile | undefined;

  margin: number | undefined;
  specialCodes: KrampSpecialCode[] = KRAMP_SPECIAL_CODES;
  searchTextSpecialCode: string = '';

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.newSpecial.sku = this.reservedSku!.sku;
    this.newSpecial.id = this.reservedSku!.id;
    this.profile = this.authService.getUserInfo();
  }

  retryFailedSpecial(failedSpecial: KrampSpecial) {
    this.newSpecial = failedSpecial;
  }

  toggleNewSpecial() {
    this.showForm = !this.showForm;
  }

  updateSpecialCode(searchString: string) {
    let results = this.specialCodes.filter(
      (specialCode) =>
        specialCode.code.includes(searchString) ||
        specialCode.name.includes(searchString)
    );
    if (results.length == 1) {
      this.newSpecial.krampSpecialCode = results[0].code;
    } else {
      this.newSpecial.krampSpecialCode = searchString;
    }
  }

  updatePricing() {
    this.newSpecial.supplierPrice = this.newSpecial.purchasePrice;
    this.updateSalesPrice();

    if (
      this.margin != undefined &&
      this.newSpecial.supplierPrice != undefined &&
      this.newSpecial.salesPrice != undefined
    ) {
      this.updateMargin();
    }
  }

  updateMargin() {
    if (
      this.newSpecial.supplierPrice != undefined &&
      this.newSpecial.salesPrice != undefined
    ) {
      let diff = this.newSpecial.salesPrice - this.newSpecial.supplierPrice;
      let margin = (diff / this.newSpecial.salesPrice) * 100;
      this.margin = Number(margin.toFixed(2));
    }
  }

  updateSalesPrice() {
    if (
      this.margin != undefined &&
      this.newSpecial.supplierPrice != undefined
    ) {
      let formattedMargin = (100 - this.margin) / 100;
      let newSalesPrice = this.newSpecial.supplierPrice / formattedMargin;
      this.newSpecial.salesPrice = Number(newSalesPrice.toFixed(4));
    }
  }

  toFixed(value: number, type: string) {
    switch (type) {
      case 'purchasePrice':
        this.newSpecial.purchasePrice = Number(value.toFixed(4));
        this.updatePricing();
        break;
      case 'supplierPrice':
        this.newSpecial.supplierPrice = Number(value.toFixed(4));
        this.updateMargin();
        break;
      case 'salesPrice':
        this.newSpecial.salesPrice = Number(value.toFixed(4));
        this.updateMargin();
        break;
      default:
        break;
    }
  }

  isFormValid(): boolean {
    return (
      this.description &&
      this.description.valid! &&
      this.newSpecial.krampSpecialCode != '' &&
      this.newSpecial.krampSupplierId != undefined &&
      this.supplierSku &&
      this.supplierSku.valid! &&
      this.purchasePrice &&
      this.purchasePrice.valid! &&
      this.supplierPrice &&
      this.supplierPrice.valid! &&
      this.salesPrice &&
      this.salesPrice.valid! &&
      this.margin! >= 0 &&
      this.margin! <= 99.99 &&
      this.orderUnit &&
      this.orderUnit.valid! &&
      this.deliveryDays &&
      this.deliveryDays.valid!
    );
  }

  createSpecial(special: KrampSpecial, keepInputs: boolean) {
    special.id = this.reservedSku.id;
    special.sku = this.reservedSku.sku;
    special.creationBy = `${this.profile?.firstName} ${this.profile?.lastName}`;
    this.keepInputs.emit(keepInputs);
    this.createNewSpecial.emit(this.cleanUpSpecial(special));
  }

  clearSpecial() {
    this.newSpecial = EmptyKrampSpecial();
    this.description.reset();
    this.specialCode.reset();
    this.supplier.reset();
    this.supplierSku.reset();
    this.purchasePrice.reset();
    this.supplierPrice.reset();
    this.salesPrice.reset();
  }

  clearUniqueFields() {
    this.description.reset();
  }

  cleanUpSpecial(special: KrampSpecial): KrampSpecial {
    special.description = special.description.trim();
    special.krampSupplierSku = special.krampSupplierSku.trim();
    return special;
  }
}
